/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { FunctionComponent } from 'react'
import { Platform, useWindowDimensions } from 'react-native'
import styled, { useTheme } from 'styled-components/native'

import { getTagColors } from 'ui/designSystem/Tag/helper/getTagColors'
import { getTagIcon } from 'ui/designSystem/Tag/helper/getTagIcon'
import { renderTagIcon } from 'ui/designSystem/Tag/helper/renderTagIcon'
import { TagProps, TagVariant } from 'ui/designSystem/Tag/types'
import { Typo } from 'ui/theme'

export const Tag: FunctionComponent<TagProps> = ({
  label,
  variant = TagVariant.DEFAULT,
  Icon,
  isMultiline = false,
  ...props
}) => {
  const { fontScale } = useWindowDimensions()
  const theme = useTheme()
  const { background, icon } = theme.designSystem.color

  const FinalIcon = getTagIcon(variant, Icon)

  const { backgroundColor, iconColor, labelColor, iconSize } = getTagColors({
    variant,
    background,
    icon,
    theme,
  })

  const shouldWrap = fontScale > 1 || isMultiline

  return (
    <Wrapper
      backgroundColor={backgroundColor}
      testID="tagWrapper"
      shouldWrap={shouldWrap}
      {...props}>
      {FinalIcon ? (
        <IconContainer>{renderTagIcon(iconColor, iconSize, FinalIcon)}</IconContainer>
      ) : null}
      <LabelText color={labelColor} shouldWrap={shouldWrap}>
        {label}
      </LabelText>
    </Wrapper>
  )
}

const Wrapper = styled.View<{ backgroundColor: string; shouldWrap: boolean }>(
  ({ theme, backgroundColor, shouldWrap }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    maxWidth: shouldWrap ? '100%' : undefined,
    borderRadius: theme.designSystem.size.borderRadius.s,
    backgroundColor,
    paddingVertical: theme.designSystem.size.spacing.xxs,
    paddingHorizontal: theme.designSystem.size.spacing.xs,
  })
)

const LabelText = styled(Typo.BodyAccentXs)<{ shouldWrap: boolean }>(({ shouldWrap }) => ({
  flexShrink: shouldWrap ? 1 : undefined,
  ...(Platform.OS === 'web' && {
    textWrap: shouldWrap ? 'wrap' : 'nowrap',
    overflowWrap: shouldWrap ? 'anywhere' : undefined,
  }),
}))

const IconContainer = styled.View(({ theme }) => ({
  marginRight: theme.designSystem.size.spacing.xs,
}))
